import { useTourLifecycle } from '@shared/contexts/TourLifecycleContext'
import { driver, type Config, type Driver } from 'driver.js'
import { useCallback, useEffect, useRef } from 'react'
import { useCanStartTour } from './useCanStartTour'

interface TrackedTourOptions {
  delayMs?: number
}

export function useTrackedDriverTour() {
  const canStartTour = useCanStartTour()
  const { beginTour } = useTourLifecycle()
  const canStartRef = useRef(canStartTour)
  const activeToursRef = useRef(new Map<Driver, () => void>())
  canStartRef.current = canStartTour

  useEffect(() => {
    const activeTours = activeToursRef.current
    return () => {
      const toursToDestroy = Array.from(activeTours.entries())
      activeTours.clear()
      toursToDestroy.forEach(([tour, completeTour]) => {
        try {
          tour.destroy()
        } finally {
          completeTour()
        }
      })
    }
  }, [])

  return useCallback(
    (config: Config, options: TrackedTourOptions = {}) => {
      let cancelled = false
      let launched = false
      let timer: number | undefined

      const launch = () => {
        if (cancelled || !canStartRef.current) return
        launched = true
        const completeTour = beginTour()
        const existingOnDestroyed = config.onDestroyed
        let tour: Driver | undefined
        let suppressOnDestroyed = false

        try {
          tour = driver({
            ...config,
            onDestroyed: (element, step, driverOptions) => {
              try {
                if (!suppressOnDestroyed) {
                  existingOnDestroyed?.(element, step, driverOptions)
                }
              } finally {
                if (tour) activeToursRef.current.delete(tour)
                completeTour()
              }
            },
          })
          activeToursRef.current.set(tour, completeTour)
          tour.drive()
        } catch (error) {
          if (tour) {
            activeToursRef.current.delete(tour)
            suppressOnDestroyed = true
            try {
              tour.destroy()
            } catch {
              // Preserve the launch error; lifecycle cleanup still has to complete.
            }
          }
          completeTour()
          throw error
        }
      }

      if (options.delayMs && options.delayMs > 0) {
        timer = window.setTimeout(launch, options.delayMs)
      } else {
        launch()
      }

      return () => {
        if (cancelled || launched) return
        cancelled = true
        if (timer !== undefined) window.clearTimeout(timer)
      }
    },
    [beginTour]
  )
}
